import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { Card, PageHeader } from "../components/account-ui.tsx";
import { focusFirstError, PasswordField } from "../components/auth-form.tsx";
import { ErrorMessage, HighlightedButton } from "../components/legacy/ui.tsx";
import { CheckIcon } from "../icons/index.tsx";
import { authClient } from "../lib/auth-client.ts";
import {
  collect,
  type Errors,
  validatePassword,
  validateRequired,
} from "../lib/auth-validation.ts";

export const Route = createFileRoute("/account/password")({ component: PasswordChange });

type FormField = "currentPassword" | "newPassword";

const ORDER: readonly FormField[] = ["currentPassword", "newPassword"];

function PasswordChange() {
  const navigate = useNavigate();
  const [currentPassword, setCurrent] = useState("");
  const [newPassword, setNew] = useState("");
  const [errors, setErrors] = useState<Errors<FormField>>({});
  const [submitted, setSubmitted] = useState(false);
  const [formError, setFormError] = useState("");
  const [busy, setBusy] = useState(false);

  const check = () =>
    collect<FormField>({
      // The existing password predates our rules, so only presence is ours to check.
      currentPassword: validateRequired(currentPassword, "current password"),
      newPassword: validatePassword(newPassword),
    });

  const revalidate = () => {
    if (submitted) setErrors(check());
  };

  const updatePassword = async () => {
    setSubmitted(true);
    setFormError("");
    const found = check();
    setErrors(found);
    if (Object.keys(found).length > 0) {
      focusFirstError(found, ORDER);
      return;
    }

    setBusy(true);
    const result = await authClient.changePassword({
      currentPassword,
      newPassword,
      revokeOtherSessions: true,
    });
    setBusy(false);
    if (result.error) {
      const code = result.error.code ?? "";
      // A rejected password belongs on the field the user has to retype,
      // not in a box at the bottom of the form.
      const onField: Errors<FormField> | null =
        code.includes("INVALID_PASSWORD") || code.includes("CREDENTIALS")
          ? { currentPassword: "That is not your current password" }
          : code.includes("PASSWORD_TOO_SHORT") || code.includes("WEAK")
            ? { newPassword: "Please choose a stronger password" }
            : null;
      if (onField) {
        setErrors(onField);
        focusFirstError(onField, ORDER);
        return;
      }
      setFormError(result.error.message ?? "Could not change password");
      return;
    }
    navigate({ to: "/account" });
  };

  return (
    <div>
      <PageHeader
        title="Security"
        subtitle="Changing your password signs you out everywhere else."
      />
      <Card className="max-w-md">
        <form
          noValidate
          className="space-y-5"
          onSubmit={(e) => {
            e.preventDefault();
            updatePassword();
          }}
        >
          {formError !== "" && (
            <ErrorMessage
              heading="We ran into a problem changing your password!"
              message={formError}
            />
          )}

          <PasswordField
            label="Current password"
            name="currentPassword"
            autoComplete="current-password"
            value={currentPassword}
            error={errors.currentPassword}
            onChange={(v) => {
              setCurrent(v);
              revalidate();
            }}
            onBlur={revalidate}
          />

          <PasswordField
            label="New password"
            name="newPassword"
            autoComplete="new-password"
            showRule
            value={newPassword}
            error={errors.newPassword}
            onChange={(v) => {
              setNew(v);
              revalidate();
            }}
            onBlur={revalidate}
          />

          <HighlightedButton type="submit" disabled={busy}>
            <CheckIcon /> &nbsp; {busy ? "Saving…" : "Save"}
          </HighlightedButton>
        </form>
      </Card>
    </div>
  );
}
